import { contactsApi, edgeApi } from '@lib/apiClients';
import { microservices } from '@constants/microServices';
import { microServicesRoutes } from '@constants/microServicesRoutes';
import { useSessionStore } from '@features/auth/store/useSessionStore';
import { useTwilioStore } from '@store/twilio/useTwilioStore';
import { logWarning } from '@lib/logger/logWarning';

const SOURCE = 'pauseSdrSession';

interface SdrCallSession {
  id: string;
  status?: string;
  sdrStatus?: string | null;
  action?: { action?: string } | null;
}

/** Same shape the call cleanup and the login check write when pausing. */
export const pauseSessionBody = (sdrId: string): Record<string, unknown> => ({
  status: 'paused',
  sdrStatus: 'free',
  action: { action: 'paused', userId: sdrId },
});

const updateSessionPath = (sessionId: string): string =>
  `${microServicesRoutes.contacts.put.callSession.updateNest}/${sessionId}`;

const hangupSessionPath = (sessionId: string): string =>
  `${microServicesRoutes.contacts.put.callSession.hangupNest}/${sessionId}`;

const sdrLogPath = (sdrId: string): string =>
  `${microServicesRoutes.contacts.delete.sdrLogs.deleteSdrLog}/${sdrId}`;

const terminateConferencePath = (twilioSid: string): string =>
  `${microServicesRoutes.edge.get.twilio.terminateConference}/${twilioSid}/terminate`;

const isOnTransfer = (session: SdrCallSession): boolean => {
  const isSdrBusy = session.sdrStatus != null && session.sdrStatus !== 'free';
  const isTransferred = (session.action?.action ?? '').toLowerCase() === 'transferred';
  return isSdrBusy || isTransferred;
};

/**
 * Pauses the SDR's session (if any), frees the SDR and clears its sdrLog so
 * the agents' polling sees `paused` and locks their window. When the SDR is in
 * the middle of a transfer, the transferred call is torn down first, the same
 * way the call cleanup does it. Used before logout, while the token is still
 * valid. Best effort: never throws, so logout is never blocked.
 */
export const pauseActiveSdrSession = async (sdrId: string): Promise<void> => {
  let session: SdrCallSession | undefined;
  try {
    const { data } = await contactsApi.get<{ callSession?: SdrCallSession }>(
      `${microServicesRoutes.contacts.get.callSession.getByMultipleStatusAndSdrId}/${sdrId}?status=started&status=paused`,
    );
    session = data?.callSession;
  } catch (error) {
    await logWarning('Failed to load session before logout', SOURCE, { sdrId, error }, false);
    return;
  }

  if (session?.id == null) {
    return;
  }

  // A ready Twilio device means the SDR is reachable for transfers, so the
  // session is paused and cleaned even when it already reads as paused.
  const onTransfer = isOnTransfer(session);
  const isTwilioReady = useTwilioStore.getState().status === 'ready';
  if (session.status !== 'started' && !onTransfer && !isTwilioReady) {
    return;
  }

  if (onTransfer) {
    const { conferenceName, hangupCall } = useTwilioStore.getState();
    try {
      hangupCall();
    } catch (error) {
      await logWarning('Failed to end local Twilio call before logout', SOURCE, { sdrId, error }, false);
    }

    if (conferenceName != null && conferenceName !== '') {
      try {
        await edgeApi.post(terminateConferencePath(conferenceName));
      } catch (error) {
        await logWarning('Failed to terminate conference before logout', SOURCE, { sdrId, conferenceName, error }, false);
      }
    }

    try {
      await contactsApi.put(hangupSessionPath(session.id));
    } catch (error) {
      await logWarning('Failed to hangup call session before logout', SOURCE, { sdrId, sessionId: session.id, error }, false);
    }
  }

  try {
    await contactsApi.delete(sdrLogPath(sdrId));
  } catch (error) {
    // 404 means there was no SDR log left to clear
    const status = (error as { response?: { status?: number } }).response?.status;
    if (status !== 404) {
      await logWarning('Failed to delete SDR log before logout', SOURCE, { sdrId, error }, false);
    }
  }

  try {
    await contactsApi.put(updateSessionPath(session.id), pauseSessionBody(sdrId));
  } catch (error) {
    await logWarning('Failed to pause session before logout', SOURCE, { sdrId, sessionId: session.id, error }, false);
  }
};

/**
 * Pauses a session and clears the SDR log while the page is being unloaded
 * (tab/browser closed). With `twilioSid`, the transferred call is torn down
 * too. Axios requests are cancelled on unload and `sendBeacon` can't send an
 * Authorization header, so this uses `fetch` with `keepalive`; the requests
 * go out together because the page can't wait for one before the next.
 */
export const pauseSessionOnUnload = (sessionId: string, sdrId?: string, twilioSid?: string): void => {
  const { authToken } = useSessionStore.getState();
  if (authToken == null || authToken === '') {
    return;
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${authToken}`,
  };

  const send = (url: string, method: string, body?: unknown): void => {
    void fetch(url, {
      method,
      keepalive: true,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    }).catch(() => undefined);
  };

  try {
    if (twilioSid != null && twilioSid !== '') {
      send(`${microservices.edgeNest}${terminateConferencePath(twilioSid)}`, 'POST');
      send(`${microservices.contactsNest}${hangupSessionPath(sessionId)}`, 'PUT');
    }

    if (sdrId != null && sdrId !== '') {
      send(`${microservices.contactsNest}${sdrLogPath(sdrId)}`, 'DELETE');
    }

    send(`${microservices.contactsNest}${updateSessionPath(sessionId)}`, 'PUT', pauseSessionBody(sdrId ?? ''));
  } catch {
    // Page is going away — nothing else we can do.
  }
};
